"use client";

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import SystemAnnouncementComposer from "./components/SystemAnnouncementComposer";
import ClinicManager from "./components/ClinicManager";
import BulkMessageComposer from "./components/BulkMessageComposer";
import AppBannerManager from "./components/AppBannerManager";
import BreedManager from "./components/BreedManager";
import SpeciesManager from "./components/SpeciesManager";
import AdvertisementManager from "./components/AdvertisementManager";
import AppVersionManager from "./components/AppVersionManager";
import UserManager from "./components/UserManager";
import ReportedUserManager from "./components/ReportedUserManager";
import SuspendedUsersManager from "./components/SuspendedUsersManager";
import MutedUsersManager from "./components/MutedUsersManager";
import BannedUsersManager from "./components/BannedUsersManager";
import PostModerationManager from "./components/PostModerationManager";
import AuditLogManager from "./components/AuditLogManager";
import "./AdminDashboard.css";

const AdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { getUserType, logout } = useAuth();

  const [filter, setFilter] = useState("announcements"); // default to announcements
  const [isCmsOpen, setIsCmsOpen] = useState(false);
  const [isBreedsSubOpen, setIsBreedsSubOpen] = useState(false);
  const [isSpeciesSubOpen, setIsSpeciesSubOpen] = useState(false);
  const [isBannerSubOpen, setIsBannerSubOpen] = useState(false);
  const [isAdsSubOpen, setIsAdsSubOpen] = useState(false);

  const [isModerationOpen, setIsModerationOpen] = useState(false);
  const [isUserModerationOpen, setIsUserModerationOpen] = useState(false);
  const [isPostModerationOpen, setIsPostModerationOpen] = useState(false);


  // Sync state with URL
  useEffect(() => {
    const path = location.pathname;
    let newFilter = "all";

    if (path === "/admin/dashboard/announcements") newFilter = "announcements";
    else if (path === "/admin/dashboard/bulk-messages") newFilter = "bulk_messages";
    else if (path === "/admin/dashboard/app-version") newFilter = "app_version";
    else if (path === "/admin/dashboard/audit-logs") newFilter = "audit_logs";
    else if (path === "/admin/dashboard/moderation/users") {
      newFilter = "moderation_users";
      setIsModerationOpen(true);
      setIsUserModerationOpen(true);
    }
    else if (path === "/admin/dashboard/moderation/users/reported") {
      newFilter = "moderation_users_reported";
      setIsModerationOpen(true);
      setIsUserModerationOpen(true);
    }
    else if (path === "/admin/dashboard/moderation/users/suspended") {
      newFilter = "moderation_users_suspended";
      setIsModerationOpen(true);
      setIsUserModerationOpen(true);
    }
    else if (path === "/admin/dashboard/moderation/users/muted") {
      newFilter = "moderation_users_muted";
      setIsModerationOpen(true);
      setIsUserModerationOpen(true);
    }
    else if (path === "/admin/dashboard/moderation/users/banned") {
      newFilter = "moderation_users_banned";
      setIsModerationOpen(true);
      setIsUserModerationOpen(true);
    }
    else if (path === "/admin/dashboard/moderation/posts/all") {
      newFilter = "moderation_posts_all";
      setIsModerationOpen(true);
      setIsPostModerationOpen(true);
    }
    else if (path === "/admin/dashboard/moderation/posts/reported") {
      newFilter = "moderation_posts_reported";
      setIsModerationOpen(true);
      setIsPostModerationOpen(true);
    }

    else if (path === "/admin/dashboard/cms/banners") {
      newFilter = "cms_banners";
      setIsCmsOpen(true);
      setIsBannerSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/banners/add") {
      newFilter = "cms_banners_add";
      setIsCmsOpen(true);
      setIsBannerSubOpen(true);
    }
    else if (path.startsWith("/admin/dashboard/cms/banners/edit/")) {
      newFilter = "cms_banners_edit";
      setIsCmsOpen(true);
      setIsBannerSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/breeds") {
      newFilter = "cms_breeds_list";
      setIsCmsOpen(true);
      setIsBreedsSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/breeds/add") {
      newFilter = "cms_breeds_add";
      setIsCmsOpen(true);
      setIsBreedsSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/advertisements/add" || 
             (path.startsWith("/admin/dashboard/cms/advertisements/") && path.endsWith("/add"))) {
       newFilter = "cms_ads_add";
       setIsCmsOpen(true);
       setIsAdsSubOpen(true);
    }
    else if (path.startsWith("/admin/dashboard/cms/advertisements/edit/")) {
       newFilter = "cms_ads_edit";
       setIsCmsOpen(true);
       setIsAdsSubOpen(true);
    }
    else if (path.includes("/edit/") && path.startsWith("/admin/dashboard/cms/advertisements/")) {
      // Matches /admin/dashboard/cms/advertisements/:countryCode/edit/:adId
      newFilter = "cms_ads_edit";
      setIsCmsOpen(true);
      setIsAdsSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/advertisements" || path.startsWith("/admin/dashboard/cms/advertisements/")) {
      newFilter = "cms_ads_list";
      setIsCmsOpen(true);
      setIsAdsSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/species") {
      newFilter = "cms_species_list";
      setIsCmsOpen(true);
      setIsSpeciesSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/species/add") {
      newFilter = "cms_species_add";
      setIsCmsOpen(true);
      setIsSpeciesSubOpen(true);
    }

    else if (path === "/admin/dashboard/clinics") {
      newFilter = "clinics";
    }
    else if (path === "/admin/dashboard" || path === "/admin/dashboard/") {
      // Default landing page: redirect admin to announcements
      navigate('/admin/dashboard/announcements');
      return;
    }

    setFilter(newFilter);
  }, [location.pathname]);

  // Ensure body overflow is restored when component mounts (fix for scroll issues)
  useEffect(() => {
    // Restore body overflow on mount
    document.body.style.overflow = '';
    document.body.style.overflowY = '';

    // Cleanup: ensure overflow is restored on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.overflowY = '';
    };
  }, []);

  // Check user type on mount only
  useEffect(() => {
    const userType = getUserType();
    const isAdmin = userType === 'ADMIN';
    const isContentAdmin = userType === 'CONTENT_ADMIN';

    if (!isAdmin && !isContentAdmin) {
      navigate('/auth/login');
      return;
    }

    // Redirect CONTENT_ADMIN if they land on the base dashboard or non-CMS paths
    if (isContentAdmin && (
      location.pathname === '/admin/dashboard' || 
      location.pathname === '/admin/dashboard/' ||
      location.pathname === '/admin/dashboard/clinics' ||
      location.pathname === '/admin/dashboard/announcements'
    )) {
      navigate('/admin/dashboard/cms/banners');
    }
  }, [navigate, location.pathname]);



  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="admin-dashboard-page" dir={i18n.dir()}>
      {/* Top Header with Logout */}
      <div className="admin-dashboard-header-bar">
        <div className="admin-dashboard-header-bar-content">
          <h2 className="admin-dashboard-header-title">{t("adminDashboard.title")}</h2>
          <div className="admin-dashboard-header-actions">
            {/* Language Toggle */}
            <button
              onClick={() => {
                const newLang = i18n.language === "en" ? "ar" : "en";
                i18n.changeLanguage(newLang);
              }}
              className="admin-dashboard-language-toggle"
              title={
                i18n.language === "en"
                  ? t("language.switchToArabic")
                  : t("language.switchToEnglish")
              }
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
              <span>{i18n.language === "en" ? "AR" : "EN"}</span>
            </button>
            {getUserType() === 'ADMIN' && (
              <button className="admin-dashboard-stats-btn" onClick={() => navigate('/statistics')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="20" x2="18" y2="10"></line>
                  <line x1="12" y1="20" x2="12" y2="4"></line>
                  <line x1="6" y1="20" x2="6" y2="14"></line>
                </svg>
                <span>{t("adminDashboard.statistics")}</span>
              </button>
            )}
            <button className="admin-dashboard-logout-btn" onClick={handleLogout}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              <span>{t("adminDashboard.logout")}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="admin-dashboard-layout">
        {/* Left Sidebar Menu */}
        <aside className="admin-dashboard-sidebar">
          <nav className="admin-dashboard-sidebar-nav">
            {getUserType() === 'ADMIN' && (
              <>
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "announcements" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard/announcements")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                  <span>{t("adminDashboard.menu.announcements", "Announcements")}</span>
                </button>
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "bulk_messages" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard/bulk-messages")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  <span>{t("adminDashboard.menu.bulkMessages", "Bulk Messaging")}</span>
                </button>
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "app_version" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard/app-version")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                    <path d="M2 2l7.586 7.586"></path>
                    <circle cx="11" cy="11" r="2"></circle>
                  </svg>
                  <span>{t("adminDashboard.menu.app_version", "App Version")}</span>
                </button>
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "audit_logs" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard/audit-logs")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  <span>{t("adminDashboard.menu.auditLogs", "Audit Logs")}</span>
                </button>

                {/* Moderation Section */}
                <div className="admin-dashboard-sidebar-group">
                  <button
                    className={`admin-dashboard-sidebar-item ${filter.startsWith("moderation_") ? "active" : ""}`}
                    onClick={() => setIsModerationOpen(!isModerationOpen)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                    </svg>
                    <span>{t("adminDashboard.menu.moderation", "Moderation")}</span>
                    <svg
                      className={`chevron ${isModerationOpen ? "rotated" : ""}`}
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>

                  {isModerationOpen && (
                    <div className="admin-dashboard-sidebar-sub">
                      {/* User Moderation submenu */}
                      <button
                        className={`admin-dashboard-sidebar-item ${filter.startsWith("moderation_users") ? "active" : ""}`}
                        onClick={() => setIsUserModerationOpen(!isUserModerationOpen)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        <span>{t("adminDashboard.menu.userModeration", "User Moderation")}</span>
                        <svg
                          className={`chevron ${isUserModerationOpen ? "rotated" : ""}`}
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </button>

                      {isUserModerationOpen && (
                        <div className="admin-dashboard-sidebar-sub-nested">
                          <button
                            className={`admin-dashboard-sidebar-item ${filter === "moderation_users" ? "active" : ""}`}
                            onClick={() => navigate("/admin/dashboard/moderation/users")}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            <span>{t("adminDashboard.menu.allUsers", "All Users")}</span>
                          </button>
                          <button
                            className={`admin-dashboard-sidebar-item ${filter === "moderation_users_reported" ? "active" : ""}`}
                            onClick={() => navigate("/admin/dashboard/moderation/users/reported")}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                              <line x1="12" y1="9" x2="12" y2="13" />
                              <circle cx="12" cy="17" r="0.8" fill="currentColor" stroke="none" />
                            </svg>
                            <span>{t("adminDashboard.menu.reportedUsers", "Reported Users")}</span>
                          </button>
                          <button
                            className={`admin-dashboard-sidebar-item ${filter === "moderation_users_suspended" ? "active" : ""}`}
                            onClick={() => navigate("/admin/dashboard/moderation/users/suspended")}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="12" y1="8" x2="12" y2="12"></line>
                              <circle cx="12" cy="17" r="0.8" fill="currentColor" stroke="none" />
                            </svg>
                            <span>{t("adminDashboard.menu.suspendedUsers", "Suspended Users")}</span>
                          </button>
                          <button
                            className={`admin-dashboard-sidebar-item ${filter === "moderation_users_muted" ? "active" : ""}`}
                            onClick={() => navigate("/admin/dashboard/moderation/users/muted")}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
                              <line x1="23" y1="9" x2="17" y2="15"></line>
                              <line x1="17" y1="9" x2="23" y2="15"></line>
                            </svg>
                            <span>{t("adminDashboard.menu.mutedUsers", "Muted Users")}</span>
                          </button>
                          <button
                            className={`admin-dashboard-sidebar-item ${filter === "moderation_users_banned" ? "active" : ""}`}
                            onClick={() => navigate("/admin/dashboard/moderation/users/banned")}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                            </svg>
                            <span>{t("adminDashboard.menu.bannedUsers", "Banned Users")}</span>
                          </button>
                        </div>
                      )}

                      {/* Post Moderation submenu */}
                      <button
                        className={`admin-dashboard-sidebar-item ${filter.startsWith("moderation_posts") ? "active" : ""}`}
                        onClick={() => setIsPostModerationOpen(!isPostModerationOpen)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                        <span>{t("adminDashboard.menu.postModeration", "Post Moderation")}</span>
                        <svg
                          className={`chevron ${isPostModerationOpen ? "rotated" : ""}`}
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </button>

                      {isPostModerationOpen && (
                        <div className="admin-dashboard-sidebar-sub-nested">
                          <button
                            className={`admin-dashboard-sidebar-item ${filter === "moderation_posts_all" ? "active" : ""}`}
                            onClick={() => navigate("/admin/dashboard/moderation/posts/all")}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="8" y1="6" x2="21" y2="6"></line>
                              <line x1="8" y1="12" x2="21" y2="12"></line>
                              <line x1="8" y1="18" x2="21" y2="18"></line>
                              <line x1="3" y1="6" x2="3.01" y2="6"></line>
                              <line x1="3" y1="12" x2="3.01" y2="12"></line>
                              <line x1="3" y1="18" x2="3.01" y2="18"></line>
                            </svg>
                            <span>{t("adminDashboard.menu.allPosts", "All Posts")}</span>
                          </button>
                          <button
                            className={`admin-dashboard-sidebar-item ${filter === "moderation_posts_reported" ? "active" : ""}`}
                            onClick={() => navigate("/admin/dashboard/moderation/posts/reported")}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                              <line x1="12" y1="9" x2="12" y2="13"></line>
                              <circle cx="12" cy="17" r="0.8" fill="currentColor" stroke="none" />
                            </svg>
                            <span>{t("adminDashboard.menu.reportedPosts", "Reported Posts")}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Clinics Section */}
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "clinics" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard/clinics")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                  </svg>
                  <span>{t("adminDashboard.menu.clinics", "Clinics")}</span>
                </button>
              </>
            )}

            {/* CMS Section - Accessible to both Admin and Content Admin */}
            {(getUserType() === 'CONTENT_ADMIN' || getUserType() === 'ADMIN') && (
              <div className="admin-dashboard-sidebar-group">
                <button
                  className={`admin-dashboard-sidebar-item ${filter.startsWith("cms_") ? "active" : ""}`}
                  onClick={() => setIsCmsOpen(!isCmsOpen)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
                  </svg>
                  <span>{t("adminDashboard.menu.cms", "CMS")}</span>
                  <svg
                    className={`chevron ${isCmsOpen ? "rotated" : ""}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>

              {isCmsOpen && (
                <div className="admin-dashboard-sidebar-sub">
                  <div className="admin-dashboard-sidebar-group">
                    <button
                      className={`admin-dashboard-sidebar-item ${filter === "cms_banners" ? "active" : ""}`}
                      onClick={() => setIsBannerSubOpen(!isBannerSubOpen)}                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                      <span>{t("adminDashboard.menu.cms_banners", "App Main Banner")}</span>
                      <svg
                        className={`chevron ${isBannerSubOpen ? "rotated" : ""}`}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>
                    {isBannerSubOpen && (
                      <div className="admin-dashboard-sidebar-sub-nested">
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_banners" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/banners")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.banners_list_data", "Banners Data")}</span>
                        </button>
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_banners_add" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/banners/add")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.add_breed_data", "Add Banners Data")}</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Species Sub-section */}
                  <div className="admin-dashboard-sidebar-group">
                    <button
                      className={`admin-dashboard-sidebar-item ${filter.startsWith("cms_species") ? "active" : ""}`}
                      onClick={() => setIsSpeciesSubOpen(!isSpeciesSubOpen)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <ellipse cx="12" cy="12" rx="10" ry="6"></ellipse>
                        <path d="M2 12c0 4 4.5 7 10 7s10-3 10-7"></path>
                        <path d="M12 5v14"></path>
                      </svg>
                      <span>{t("adminDashboard.menu.cms_species", "Species")}</span>
                      <svg
                        className={`chevron ${isSpeciesSubOpen ? "rotated" : ""}`}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>

                    {isSpeciesSubOpen && (
                      <div className="admin-dashboard-sidebar-sub-nested">
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_species_list" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/species")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.species_list_data", "Species List")}</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="admin-dashboard-sidebar-group">
                    <button
                      className={`admin-dashboard-sidebar-item ${filter.startsWith("cms_breeds") ? "active" : ""}`}
                      onClick={() => setIsBreedsSubOpen(!isBreedsSubOpen)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                        <line x1="7" y1="7" x2="7.01" y2="7"></line>
                      </svg>
                      <span>{t("adminDashboard.menu.cms_breeds", "Breeds")}</span>
                      <svg
                        className={`chevron ${isBreedsSubOpen ? "rotated" : ""}`}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>

                    {isBreedsSubOpen && (
                      <div className="admin-dashboard-sidebar-sub-nested">
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_breeds_list" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/breeds")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.breeds_list_data", "Breeds Data")}</span>
                        </button>
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_breeds_add" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/breeds/add")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.add_breed_data", "Add Breed Data")}</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="admin-dashboard-sidebar-group">
                    <button
                      className={`admin-dashboard-sidebar-item ${filter.startsWith("cms_ads") ? "active" : ""}`}
                      onClick={() => setIsAdsSubOpen(!isAdsSubOpen)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                        <line x1="8" y1="21" x2="16" y2="21"></line>
                        <line x1="12" y1="17" x2="12" y2="21"></line>
                        <path d="M7 8h10M7 12h5"></path>
                      </svg>
                      <span>{t("adminDashboard.menu.cms_advertisements", "Advertisements")}</span>
                      <svg
                        className={`chevron ${isAdsSubOpen ? "rotated" : ""}`}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>

                    {isAdsSubOpen && (
                      <div className="admin-dashboard-sidebar-sub-nested">
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_ads_list" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/advertisements")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.ads_list_data", "Advertisements Data")}</span>
                        </button>
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_ads_add" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/advertisements/add")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.add_ad_data", "Add Advertisement")}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </nav>
          </aside>

        {/* Main Content */}
        <div className="admin-dashboard-container">
          {filter === "announcements" ? (
            <SystemAnnouncementComposer />
          ) : filter === "bulk_messages" ? (
            <BulkMessageComposer />
          ) : filter === "app_version" ? (
            <AppVersionManager />
          ) : filter.startsWith("cms_banners") ? (
            <AppBannerManager initialView={filter === "cms_banners_add" ? "add" : filter === "cms_banners_edit" ? "edit" : "list"} />
          ) : filter.startsWith("cms_breeds") ? (
            <BreedManager initialView={filter === "cms_breeds_add" ? "form" : "list"} />
          ) : filter.startsWith("cms_ads") ? (
            <AdvertisementManager initialView={(filter === "cms_ads_add" || filter === "cms_ads_edit") ? "form" : "summary"} />
          ) : filter.startsWith("cms_species") ? (
            <SpeciesManager initialView={filter === "cms_species_add" ? "form" : "list"} />
          ) : filter === "moderation_users" ? (
            <UserManager />
          ) : filter === "moderation_users_reported" ? (
            <ReportedUserManager />
          ) : filter === "moderation_users_suspended" ? (
            <SuspendedUsersManager />
          ) : filter === "moderation_users_muted" ? (
            <MutedUsersManager />
          ) : filter === "moderation_users_banned" ? (
            <BannedUsersManager />
          ) : filter === "moderation_posts_all" ? (
            <PostModerationManager mode="all" />
          ) : filter === "moderation_posts_reported" ? (
            <PostModerationManager mode="reported" />
          ) : filter === "clinics" ? (
            <ClinicManager />
          ) : filter === "audit_logs" ? (
            <AuditLogManager />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

